import {
  Application,
  utils,
  Graphics,
  Sprite,
  Point,
  Text,
  filters,
  Container,
} from 'pixi.js';
import { RGBSplitFilter } from '@pixi/filter-rgb-split';
import { debounce, random, clamp } from 'lodash';
import { layout } from './layout';
import aion from '@/scripts/aion';
import hades from '@/scripts/hades';
import apollo from '@/scripts/apollo';
import animations from '@/assets/animations';
import SplitFilter from '@/scripts/SplitFilter';
import u from '@/scripts/utils';

class Stage {
  constructor(DOMElement, DOMCanvas, DOMContainer) {
    this.DOMElement = DOMElement;
    this.DOMCanvas = DOMCanvas;
    this.DOMContainer = DOMContainer;
    this.time = 0;
    this.flowers = [];
    this.interval = null;

    utils.skipHello();
    this.app = new Application({
      view: this.DOMCanvas,
      resizeTo: this.DOMElement,
      resolution: clamp(devicePixelRatio, 1, 1.5),
      autoDensity: true,
      antialias: true,
      transparent: true,
    });

    layout.calculateRatio();
    window.addEventListener('resize', debounce(() => this.resize(), 600));
  }

  load() {
    this.videoElement = document.createElement('video');
    this.videoElement.crossOrigin = 'anonymous';
    this.videoElement.autoload = false;
    this.videoElement.preload = '';
    this.videoElement.muted = true;
    this.videoElement.autoplay = true;
    this.videoElement.loop = true;
    this.videoElement.src = '/static/textures/video.mp4';
    this.app.loader.add('displace', '/static/textures/displace.png');
    this.app.loader.add('red-noise', '/static/textures/red-noise.png');
    this.app.loader.add('stone', '/static/textures/stone.png');
    for (let i = 1; i <= 16; i += 1) {
      this.app.loader.add(`flower-${i}`, `/static/textures/flower-${i}.png`);
    }
    return new Promise(resolve => this.app.loader.load(() => resolve()));
  }

  init() {
    this.background = new Graphics();
    this.background
      .beginFill(0x231f20)
      .drawRect(
        0, 0,
        this.app.view.width,
        this.DOMContainer.getBoundingClientRect().height,
      )
      .endFill();
    this.app.stage.addChild(this.background);

    this.createDisplaceFilter();
    this.createSplitScrollFilter();
    this.createRedNoise();
    this.createBadThings();
    this.createAStatementTitle();
    this.createShare();
    this.createViolence();
    this.createVideo();
    this.createStone();

    this.calculatePositions();

    document.body.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      this.popFlower();
      this.interval = setInterval(() => this.popFlower(), 100);
    });

    document.body.addEventListener('touchstart', () => {
      this.popFlower();
      this.interval = setInterval(() => this.popFlower(), 100);
    });

    Array.from(document.querySelectorAll('a')).forEach((a) => {
      a.addEventListener('mousedown', event => event.stopPropagation());
    });

    document.body.addEventListener('mouseup', () => { clearInterval(this.interval); });
    document.body.addEventListener('touchend', () => { clearInterval(this.interval); });

    aion.add(time => this.render(time), 'render');
  }

  render(time) {
    this.time += time;
    this.app.stage.position.y = -hades.ref.amount.y;

    // Parallax red
    const scaledHadesAmount = hades.ref.amount.y * 0.1;
    this.redNoise.position.y = this.redNoise.originalY + scaledHadesAmount;

    // Parallax video
    this.video.position.y = this.video.originalY + scaledHadesAmount;

    // Move displacement
    this.displaceSprite.position.x = apollo.ref.cursor.x + hades.ref.amount.x;
    this.displaceSprite.position.y = apollo.ref.cursor.y + hades.ref.amount.y;
    this.displaceNoiseFilter.scale.x = 50 + Math.abs(apollo.ref.velocity.x * 100);
    this.displaceNoiseFilter.scale.y = 50 + Math.abs(apollo.ref.velocity.y * 100);
    this.splitFilter.scales.red = 50 + Math.abs(Math.max(apollo.ref.velocity.x, apollo.ref.velocity.y));
    this.scrollSplitFilter.red.y = (hades.ref.velocity.y * hades.ref.direction.y) * 10;
  }

  resize() {
    layout.calculateRatio();
    hades.resize();
    this.calculatePositions();
    this.background.width = layout.viewport.actual.width;
    this.background.height = this.DOMContainer.getBoundingClientRect().height;
  }

  calculatePositions() {
    this.redNoise.scale = new Point(
      layout.viewport.clampedRatio.width,
      layout.viewport.clampedRatio.width,
    );
    this.redNoise.position.y = layout.viewport.actual.height + 300;
    this.redNoise.originalY = this.redNoise.position.y - 100;
    this.redNoise.position.x = layout.viewport.actual.width - (
      this.redNoise.width + (130 * layout.viewport.clampedRatio.width)
    );

    this.badThings.style.fontSize = 120 * layout.viewport.clampedRatio.width;
    this.badThings.position.x = this.redNoise.position.x;
    this.badThings.position.y = this.redNoise.position.y + this.redNoise.height + (250 * layout.viewport.clampedRatio.width);

    this.stone.scale = new Point(
      layout.viewport.clampedRatio.width,
      layout.viewport.clampedRatio.width,
    );
    this.stone.position.x = this.redNoise.position.x + (this.redNoise.width / 3);
    this.stone.position.y = (this.redNoise.position.y + this.redNoise.height) - (this.redNoise.width / 1.8);

    const violenceFont = window.innerWidth < 720 ? 110 : 120;
    this.violenceLines.forEach((line, index) => {
      line.style.fontSize = violenceFont * layout.viewport.clampedRatio.width;
      line.dirty = true;
      line.position.y = ((line.height - (45 * layout.viewport.clampedRatio.width)) * index);
    });
    const violenceTextAdderX = window.innerWidth < 720 ? 50 : 150;
    const violenceTextAdderY = window.innerWidth < 720 ? 650 : 850;
    this.violenceText.position.y = this.redNoise.position.y + (violenceTextAdderX * layout.viewport.clampedRatio.width);
    this.violenceText.position.x = layout.viewport.actual.width - this.violenceText.width - (violenceTextAdderY * layout.viewport.clampedRatio.width);

    this.aStatementTitle.style.fontSize = 350 * layout.viewport.clampedRatio.width;
    this.aStatementTitle.style.lineHeight = 300 * layout.viewport.clampedRatio.width;
    this.aStatementTitle.dirty = true;
    this.aStatementTitle.position.x = layout.padding.left;
    const hashTitleTop = document.querySelector('.hashtag .title').getBoundingClientRect().top;
    this.aStatementTitle.position.y = hashTitleTop + hades.ref.amount.y;

    this.share.style.fontSize = 350 * layout.viewport.clampedRatio.width;
    this.share.style.lineHeight = 300 * layout.viewport.clampedRatio.width;
    this.share.dirty = true;
    this.share.position.x = layout.padding.left;
    const shareTitleTop = document.querySelector('.share .title').getBoundingClientRect().top;
    this.share.position.y = shareTitleTop + hades.ref.amount.y;

    this.theAbodeOfPeace.style.fontSize = 350 * layout.viewport.clampedRatio.width;
    this.theAbodeOfPeace.style.lineHeight = 300 * layout.viewport.clampedRatio.width;
    this.theAbodeOfPeace.dirty = true;
    this.theAbodeOfPeace.position.x = layout.padding.left;
    const videoTitleTop = document.querySelector('.video .title').getBoundingClientRect().top;
    this.theAbodeOfPeace.position.y = videoTitleTop + hades.ref.amount.y;

    this.video.scale = new Point(
      4 * layout.viewport.clampedRatio.width,
      4 * layout.viewport.clampedRatio.width,
    );
    this.video.position.x = layout.viewport.actual.width - this.video.width;
    const video = document.querySelector('section.video');
    const fromTop = video.getBoundingClientRect().top + hades.ref.amount.y;
    this.video.position.y = fromTop + (this.video.height / 8) + 50 * layout.viewport.clampedRatio.width;
    this.video.originalY = this.video.position.y;
  }

  createRedNoise() {
    this.redNoise = new Sprite(this.app.loader.resources['red-noise'].texture);
    this.redNoise.filters = [this.displaceNoiseFilter];
    this.app.stage.addChild(this.redNoise);
  }

  createDisplaceFilter() {
    this.displaceSprite = new Sprite(this.app.loader.resources['displace'].texture);
    this.displaceSprite.anchor = new Point(0.5, 0.5);
    this.displaceSprite.scale = new Point(2, 2);
    this.app.stage.addChild(this.displaceSprite);
    this.displaceNoiseFilter = new filters.DisplacementFilter(this.displaceSprite, 0);
    this.displaceNoiseFilter.padding = 300;
    this.splitFilter = new SplitFilter(this.displaceSprite, 0, 0, 0);
  }

  createBadThings() {
    this.badThings = new Text('(click to stop)', {
      fill: 0xffffff,
      fontFamily: 'GT America',
      fontSize: 120 * layout.viewport.clampedRatio.width,
    });
    this.app.stage.addChild(this.badThings);
  }

  createStone() {
    this.stoneContainer = new Container();
    this.stone = new Sprite(this.app.loader.resources['stone'].texture);
    this.rgbSplit = new RGBSplitFilter(new Point(0, 0), new Point(0, 0), new Point(0, 0));
    animations.get('rgb-glitch')(this.rgbSplit).play();
    animations.get('stone-floating')(this.stoneContainer).play();
    this.stone.filters = [this.rgbSplit];
    this.stoneContainer.addChild(this.stone);
    this.app.stage.addChild(this.stoneContainer);
  }

  createViolence() {
    this.violenceText = new Container();
    this.violenceLines = [];
    for (let i = 0; i < 4; i += 1) {
      const t = new Text('VIOLENCE', {
        fontSize: 120 * layout.viewport.clampedRatio.width,
        fontFamily: 'GT America Expanded',
        fill: null,
        strokeThickness: 2,
        stroke: 0xffffff,
      });
      t.position.y += ((t.height - 35) * i);
      this.violenceLines.push(t);
      this.violenceText.addChild(t);
    }
    this.app.stage.addChild(this.violenceText);
    animations.get('violence-loop')(this.violenceLines).play();
  }

  createAStatementTitle() {
    this.aStatementTitle = new Text('A state\n  -ment\nof disagree\n    -ment', {
      fontSize: 350 * layout.viewport.clampedRatio.width,
      fontFamily: 'GT America',
      fill: 0xffffff,
      lineHeight: 300 * layout.viewport.clampedRatio.width,
    });
    this.aStatementTitle.filters = [this.displaceNoiseFilter, this.scrollSplitFilter];
    this.app.stage.addChild(this.aStatementTitle);
  }

  createShare() {
    this.share = new Text('  A Call\nto Arms for\n   Change.', {
      fontSize: 350 * layout.viewport.clampedRatio.width,
      fontFamily: 'GT America',
      fill: 0xffffff,
      lineHeight: 300 * layout.viewport.clampedRatio.width,
    });
    this.share.filters = [this.displaceNoiseFilter, this.scrollSplitFilter];
    this.app.stage.addChild(this.share);
  }

  createSplitScrollFilter() {
    this.scrollSplitFilter = new RGBSplitFilter(
      new Point(0, 0),
      new Point(0, 0),
      new Point(0, 0),
    );
  }

  createVideo() {
    this.video = Sprite.from(this.videoElement);
    this.colorCorrection = new filters.ColorMatrixFilter();
    this.colorCorrection.contrast(0.7, true);
    this.colorCorrection.brightness(0.45, true);
    this.video.filters = [this.colorCorrection, this.displaceNoiseFilter];
    this.theAbodeOfPeace = new Text('  The\n     Abode\n  Of\nPeace?', {
      fontSize: 350 * layout.viewport.clampedRatio.width,
      fontFamily: 'GT America',
      fill: 0xffffff,
      lineHeight: 300 * layout.viewport.clampedRatio.width,
    });
    this.theAbodeOfPeace.filters = [this.displaceNoiseFilter, this.scrollSplitFilter];
    this.app.stage.addChild(this.video);
    this.app.stage.addChild(this.theAbodeOfPeace);
  }

  popFlower() {
    if (u.isTouchDevice() && this.flowers.length > 10) return;
    const flower = Sprite.from(`/static/textures/flower-${random(1, 16)}.png`);
    flower.anchor = new Point(0.5, 0.5);
    flower.scale = new Point(0.6 * layout.viewport.clampedRatio.width, 0.6 * layout.viewport.clampedRatio.width);
    if (u.isTouchDevice()) {
      flower.scale = new Point(1 * layout.viewport.clampedRatio.width, 1 * layout.viewport.clampedRatio.width);
    }
    flower.x = apollo.ref.cursor.x;
    flower.y = hades.ref.amount.y + apollo.ref.cursor.y;
    this.stoneContainer.addChild(flower);
    const audio = new Audio(`/static/sounds/fx${random(3, 6)}.mp3`);
    audio.preload = 'auto';
    audio.play();
    if (this.flowers.length < (u.isTouchDevice() ? 10 : 100)) {
      animations.get('flower-atach')(flower, this.stone).play();
    } else {
      animations.get('flower-fall')(flower, this.stone).play();
    }
    this.flowers.push(flower);
  }
}

export default Stage;
